import { getCompanyLogoCandidateUrls } from "@helpers/cdnImagePaths"
import { LoadingCircle } from "@ui-lib/src"
import { type ReactElement, useEffect, useState } from "react"

type CompanyLogoProps = {
	companyName: string
}

// created by AI: Cache logo resolution for the current browser tab so returning
// to the clients page does not re-probe extensions we have already tested.
type CompanyLogoResolution =
	| {
			kind: "loaded"
			imageSrc: string
	  }
	| {
			kind: "missing"
	  }

// created by AI: This is intentionally in-memory only. A hard refresh should ask
// the CDN again, while normal in-app navigation can reuse known logo outcomes.
const companyLogoResolutionCache = new Map<string, CompanyLogoResolution>()

// created by AI: Resolve all possible logo extensions in parallel. This prevents
// a missing png/svg from blocking the real logo behind it during first page load.
const resolveCompanyLogo = (
	companyName: string,
	onResolve: (resolution: CompanyLogoResolution) => void
): (() => void) => {
	const imageCandidates = getCompanyLogoCandidateUrls(companyName)
	let hasResolved = false
	let failedCount = 0
	const imageElements: HTMLImageElement[] = []

	for (const imageSrc of imageCandidates) {
		const imageElement = new Image()
		imageElements.push(imageElement)
		imageElement.onload = () => {
			if (hasResolved) {
				return
			}
			hasResolved = true
			onResolve({ kind: "loaded", imageSrc })
		}
		imageElement.onerror = () => {
			failedCount += 1
			if (!hasResolved && failedCount === imageCandidates.length) {
				hasResolved = true
				onResolve({ kind: "missing" })
			}
		}
		imageElement.src = imageSrc
	}

	return () => {
		hasResolved = true
		for (const imageElement of imageElements) {
			imageElement.onload = null
			imageElement.onerror = null
		}
	}
}

// created by AI: Try every supported logo extension before falling back to the
// company name text, which is the existing placeholder behavior for unknown logos.
const CompanyLogo = ({ companyName }: CompanyLogoProps): ReactElement => {
	const [logoResolution, setLogoResolution] = useState<CompanyLogoResolution | undefined>(() =>
		companyLogoResolutionCache.get(companyName)
	)

	useEffect(() => {
		const cachedResolution = companyLogoResolutionCache.get(companyName)
		if (cachedResolution !== undefined) {
			setLogoResolution(cachedResolution)
			return
		}

		setLogoResolution(undefined)
		return resolveCompanyLogo(companyName, (resolution) => {
			companyLogoResolutionCache.set(companyName, resolution)
			setLogoResolution(resolution)
		})
	}, [companyName])

	if (logoResolution?.kind === "missing") {
		return (
			<h4 className="text-title2 absolute top-1/2 left-1/2 m-0 w-4/5 -translate-x-1/2 -translate-y-1/2 text-center">
				{companyName}
			</h4>
		)
	}

	if (logoResolution === undefined) {
		return (
			<div className="absolute inset-0 flex items-center justify-center">
				<LoadingCircle size="sm" />
			</div>
		)
	}

	return (
		<img
			src={logoResolution.imageSrc}
			alt={companyName}
			className="text-title2 absolute top-1/2 left-1/2 m-0 h-4/5 w-4/5 -translate-x-1/2 -translate-y-1/2 object-contain text-center"
		/>
	)
}

// created by AI: Keep the existing helper API while delegating image probing to a
// component, because React state is needed to advance through CDN extensions.
export const getCompanyLogo = (companyName: string): ReactElement => {
	return (
		<CompanyLogo
			key={companyName}
			companyName={companyName}
		/>
	)
}
